import type { JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import FieldError from '@/forms/FieldError';
import { useBillingItemRefresh } from '@/hooks/useBillingItemRefresh';
import { useMarkAsNonBillable, useNonBillableReasons } from '@/hooks/useEncounters';
import { ENCOUNTER_TYPES } from '@/types/encounter';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Label from '@/ui/components/Form/Label';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import Select from '@/ui/components/Form/Select';
import Spinner from '@/ui/components/Spinner';

import { FormInput } from './types';

const EncounterMarkNonBillable = (): JSX.Element | null => {
  const { encounterId } = useParams();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();

  const { data: nonBillableReasons, isLoading: isLoadingNonBillableReasons } =
    useNonBillableReasons();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch
  } = useForm<FormInput>();
  const values = watch();

  const { mutateAsync: markAsNonBillable, isPending } = useMarkAsNonBillable({
    encounterId: encounterId as string,
    encounterType: state?.encounterType as ENCOUNTER_TYPES
  });

  const { mutateAsync: refreshBillingItem, isPending: isPendingRefresh } = useBillingItemRefresh();

  const saveStatus = async () => {
    const data = {
      non_billable_reason_id: values.reason.value,
      ...(values.description && { note: values.description })
    };
    await markAsNonBillable(data);

    const billingItemId = searchParams.get('billing_item_id');
    if (billingItemId) {
      await refreshBillingItem({ billingItemId });
    }
    return;
  };

  if (isLoadingNonBillableReasons)
    return (
      <div className="flex justify-center mt-4">
        <Spinner />
      </div>
    );

  return (
    <Card className="p-4 flex flex-col">
      <form className="space-y-4" onSubmit={handleSubmit(saveStatus)}>
        <Controller
          name="reason"
          control={control}
          rules={{
            required: 'Reason is required'
          }}
          render={({ field: { onChange, value, name } }) => (
            <Select
              label="Reason"
              name={name}
              onChange={onChange}
              options={nonBillableReasons ?? []}
              placeholder="Select an option"
              selectedOption={value ? value : emptySelectOption}
            />
          )}
        />
        <FieldError message={errors.reason?.message} />
        <Label htmlFor="id-description" text="Description">
          <Controller
            name="description"
            control={control}
            render={({ field: { onChange, value } }) => {
              return <RichTextEditor onChange={onChange} value={value || ''} />;
            }}
          />
        </Label>

        <Button
          className="w-full mt-4 rounded-lg"
          color={Color.YELLOW}
          disabled={isPending || isPendingRefresh}
          type="submit"
          loadingWithSpinner={isPending || isPendingRefresh}>
          {isPending || isPendingRefresh ? <Spinner /> : 'Mark As Non Billable'}
        </Button>
      </form>
    </Card>
  );
};

export default EncounterMarkNonBillable;
